import {useRef} from 'react';

import {useLazyGetWatermarkSamplePhotoQuery} from '@dms/api/core';
import {handleApiError, thumbnailUrl} from '@dms/shared';

import {ApiError} from 'shared/web';

// A BE without the sample photo endpoint (404/405), no network, or a body that does not parse.
// transformResponse throws the Zod error, so RTK Query rejects with it serialized.
const isExpectedSamplePhotoError = (error: ApiError) =>
  ('status' in error &&
    (error.status === 404 ||
      error.status === 405 ||
      error.status === 'FETCH_ERROR' ||
      error.status === 'PARSING_ERROR')) ||
  ('name' in error && error.name === 'ZodError');

/**
 * Returns a getter of the photo that an unsaved watermark is previewed on: the BE sample photo of
 * the saved settings preview. Without the sample photo the preview falls back to the reference
 * photo of the composition editor; an unexpected error is reported once per mount.
 */
export function useGetPreviewPhotoUrl() {
  const [getWatermarkSamplePhoto] = useLazyGetWatermarkSamplePhotoQuery();
  const isErrorReportedRef = useRef(false);

  return () =>
    getWatermarkSamplePhoto(undefined, true)
      .unwrap()
      .then((samplePhoto) => samplePhoto.base64Url)
      .catch((error: ApiError) => {
        if (!isExpectedSamplePhotoError(error) && !isErrorReportedRef.current) {
          isErrorReportedRef.current = true;
          handleApiError(error);
        }
        return `/${thumbnailUrl}`;
      });
}
